import { useAuiState } from '@assistant-ui/react';
import type { CitationRef } from '@iq/contracts/v1/chat';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

type CitationsByMessage = Map<string, Map<number, CitationRef>>;

interface CitationStoreValue {
  get: (messageId: string, refId: number) => CitationRef | undefined;
  /** Return all citations attached to a message, in 1-based insertion order. */
  getAll: (messageId: string) => ReadonlyArray<CitationRef>;
  setMessage: (messageId: string, citations: ReadonlyArray<CitationRef>) => void;
}

const CitationStoreContext = createContext<CitationStoreValue | null>(null);

function indexCitations(citations: ReadonlyArray<CitationRef>): Map<number, CitationRef> {
  const inner = new Map<number, CitationRef>();
  citations.forEach((c, i) => inner.set(i + 1, c));
  return inner;
}

export interface CitationStoreProviderProps {
  children: ReactNode;
  /**
   * Optional seed pulled from history (so the store renders citations on
   * first paint after navigating to a thread, before any data-part is
   * received from a live stream). Keyed by message id. Accepts `undefined`
   * explicitly to satisfy `exactOptionalPropertyTypes`.
   */
  initial?: Record<string, ReadonlyArray<CitationRef>> | undefined;
}

export function CitationStoreProvider({ children, initial }: CitationStoreProviderProps) {
  const [store, setStore] = useState<CitationsByMessage>(() => {
    const map: CitationsByMessage = new Map();
    if (initial) {
      for (const [messageId, citations] of Object.entries(initial)) {
        map.set(messageId, indexCitations(citations));
      }
    }
    return map;
  });

  const setMessage = useCallback((messageId: string, citations: ReadonlyArray<CitationRef>) => {
    setStore((prev) => {
      const next = new Map(prev);
      next.set(messageId, indexCitations(citations));
      return next;
    });
  }, []);

  const get = useCallback(
    (messageId: string, refId: number): CitationRef | undefined => {
      return store.get(messageId)?.get(refId);
    },
    [store],
  );

  const getAll = useCallback(
    (messageId: string): ReadonlyArray<CitationRef> => {
      const inner = store.get(messageId);
      if (!inner) return [];
      // Map iteration preserves insertion order — and the store inserts
      // citations in their already-deduped, first-seen order from
      // `validateCitationMarkers`. So callers don't need to dedupe again.
      return Array.from(inner.values());
    },
    [store],
  );

  const value = useMemo<CitationStoreValue>(
    () => ({ get, getAll, setMessage }),
    [get, getAll, setMessage],
  );

  return <CitationStoreContext.Provider value={value}>{children}</CitationStoreContext.Provider>;
}

export function useCitationStore(): CitationStoreValue {
  const ctx = useContext(CitationStoreContext);
  if (!ctx) {
    throw new Error('useCitationStore must be used inside <CitationStoreProvider>');
  }
  return ctx;
}

/**
 * Look up a citation for the currently-rendered assistant message. Designed
 * for components mounted inside an assistant-ui `MessagePrimitive` subtree
 * (so `useAuiState(s => s.message.id)` resolves). When called outside an
 * AuiProvider (tests that render `<CitationChip>` standalone, or future
 * non-runtime call sites), returns `undefined` rather than throwing.
 */
export function useCurrentMessageCitation(refId: number): CitationRef | undefined {
  const ctx = useContext(CitationStoreContext);
  let messageId: string | undefined;
  try {
    messageId = useAuiState((s) => s.message.id);
  } catch {
    messageId = undefined;
  }
  if (!ctx || !messageId) return undefined;
  return ctx.get(messageId, refId);
}

/**
 * Iterate all citations attached to the currently-rendered assistant
 * message. Used by the `CitationFooter` to render one badge per unique
 * source. Returns an empty array when the hook is called outside an
 * assistant-ui `MessagePrimitive` subtree.
 */
export function useCurrentMessageCitations(): ReadonlyArray<CitationRef> {
  const ctx = useContext(CitationStoreContext);
  let messageId: string | undefined;
  try {
    messageId = useAuiState((s) => s.message.id);
  } catch {
    messageId = undefined;
  }
  if (!ctx || !messageId) return [];
  return ctx.getAll(messageId);
}
